import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { type ApiResponse, type FiscalProfile, type RegistroRitenuta, type CassaProfessionale, type FiscalDocumentType, RegimeFiscale } from '@klaro7/shared-types';

export { RegimeFiscale };

import api, { tokenManager } from '@/lib/api';
import { downloadBlob } from '@/lib/download';

/**
 * Lightweight payout list item returned by GET /payouts/me/list.
 * Mirrors the backend DTO (PayoutListItem) — it intentionally omits heavier
 * fields (earningIds/currency/paidBy/paymentReference) present on the full Payout.
 */
export interface PayoutListItem {
  id: string;
  periodFrom: string;
  periodTo: string;
  totalGross: number;
  ritenuta: number;
  netto: number;
  cassaProfessionale: number;
  marcaDaBollo: boolean;
  documentType: 'proforma' | 'ritenuta';
  receiptNumber: number | null;
  proformaNumber: number | null;
  hasReceipt: boolean;
  hasProforma: boolean;
  invoice: { originalName: string; uploadedAt: string } | null;
  /** Ricevuta per ritenuta firmata caricata dal pro (solo documentType 'ritenuta'). */
  signedReceipt?: { originalName: string; uploadedAt: string; verifiedAt: string | null } | null;
  /** 'missing' | 'uploaded' | 'verified' per le ritenute, null per i proforma. */
  receiptStatus?: SignedReceiptStatusValue | null;
  confirmedAt: string | null;
  status: string;
  paidAt: string | null;
  createdAt: string;
}

export type SignedReceiptStatusValue = 'missing' | 'uploaded' | 'verified';

/** Filtri dell'elenco compensi del professionista (tutti opzionali, '' = assente). */
export interface MyPayoutFilters {
  status?: string;
  documentType?: string;
  from?: string;
  to?: string;
}

// ── query keys ───────────────────────────────────────────────────────────────
export const fiscalQueryKeys = {
  profile: ['fiscal', 'me', 'profile'] as const,
  // Prefisso: invalidarlo aggiorna l'elenco con qualunque combinazione di filtri.
  payouts: ['payouts', 'me', 'list'] as const,
  payoutsFiltered: (filters: MyPayoutFilters) => ['payouts', 'me', 'list', filters] as const,
};

/** Solo i parametri valorizzati: la query string resta pulita e la cache stabile. */
export function compactParams<T extends object>(filters: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(filters) as [keyof T, unknown][]) {
    if (v !== undefined && v !== null && v !== '') out[k] = v as T[keyof T];
  }
  return out;
}

// ── service functions ─────────────────────────────────────────────────────────
async function fetchFiscalProfile(): Promise<FiscalProfile> {
  const response = await api.get<ApiResponse<FiscalProfile>>('/fiscal/me/profile');
  return response.data.data;
}

export interface UpdateFiscalProfilePayload {
  codiceFiscale: string;
  dataNascita: string;
  comuneNascita: string;
  indirizzoResidenza: string;
  regimeFiscale: RegimeFiscale;
  partitaIVA?: string;
  paeseEstero?: string | null;
  cassaProfessionale?: CassaProfessionale | null;
  ragioneSociale?: string | null;
  sedeFiscale?: string | null;
  numeroREA?: string | null;
}

async function updateFiscalProfile(data: UpdateFiscalProfilePayload): Promise<FiscalProfile> {
  const response = await api.put<ApiResponse<FiscalProfile>>('/fiscal/me/profile', data);
  return response.data.data;
}

// ── Identity documents ─────────────────────────────────────────────────────────
async function uploadFiscalDocument(docType: FiscalDocumentType, file: File): Promise<FiscalProfile> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post<ApiResponse<FiscalProfile>>(
    `/fiscal/me/documents/${docType}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data.data;
}

async function deleteFiscalDocument(docType: FiscalDocumentType): Promise<FiscalProfile> {
  const response = await api.delete<ApiResponse<FiscalProfile>>(`/fiscal/me/documents/${docType}`);
  return response.data.data;
}

/** Fetch a stored document as a blob (auth header attached by the api interceptor). */
export async function fetchFiscalDocumentBlob(docType: FiscalDocumentType): Promise<Blob> {
  const response = await api.get(`/fiscal/me/documents/${docType}`, { responseType: 'blob' });
  return response.data as Blob;
}

async function fetchMyPayouts(filters: MyPayoutFilters = {}): Promise<PayoutListItem[]> {
  const response = await api.get<ApiResponse<PayoutListItem[]>>('/payouts/me/list', {
    params: compactParams(filters),
  });
  return response.data.data;
}

// ── Ricevuta per ritenuta firmata (pro occasionale) ────────────────────────────
async function uploadSignedReceiptReq(payoutId: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);
  await api.post(`/payouts/me/${payoutId}/signed-receipt`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

async function deleteSignedReceiptReq(payoutId: string): Promise<void> {
  await api.delete(`/payouts/me/${payoutId}/signed-receipt`);
}

/** Scarica la propria ricevuta firmata come blob (auth header dall'interceptor). */
export async function fetchMySignedReceiptBlob(payoutId: string): Promise<Blob> {
  const res = await api.get(`/payouts/me/${payoutId}/signed-receipt`, { responseType: 'blob' });
  return res.data as Blob;
}

export function useUploadSignedReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payoutId, file }: { payoutId: string; file: File }) => uploadSignedReceiptReq(payoutId, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fiscalQueryKeys.payouts }),
  });
}

export function useDeleteSignedReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payoutId: string) => deleteSignedReceiptReq(payoutId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fiscalQueryKeys.payouts }),
  });
}

export function receiptDownloadUrl(payoutId: string): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';
  return `${base}/payouts/me/receipt/${payoutId}`;
}

// ── Payout confirmation + invoice upload (professional self-service) ─────────────
async function confirmPayoutReq(payoutId: string): Promise<void> {
  await api.post(`/payouts/me/${payoutId}/confirm`);
}

async function uploadPayoutInvoiceReq(payoutId: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);
  await api.post(`/payouts/me/${payoutId}/invoice`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/** Fetch the proforma PDF as a blob (auth header attached by the api interceptor). */
export async function fetchProformaBlob(payoutId: string): Promise<Blob> {
  const res = await api.get(`/payouts/me/${payoutId}/proforma`, { responseType: 'blob' });
  return res.data as Blob;
}

export function useConfirmPayout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payoutId: string) => confirmPayoutReq(payoutId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fiscalQueryKeys.payouts }),
  });
}

export function useUploadPayoutInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payoutId, file }: { payoutId: string; file: File }) => uploadPayoutInvoiceReq(payoutId, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fiscalQueryKeys.payouts }),
  });
}

// ── hooks ─────────────────────────────────────────────────────────────────────
export function useFiscalProfile() {
  return useQuery({
    queryKey: fiscalQueryKeys.profile,
    queryFn: fetchFiscalProfile,
  });
}

export function useUpdateFiscalProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateFiscalProfilePayload) => updateFiscalProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fiscalQueryKeys.profile });
    },
  });
}

export function useMyPayouts(filters: MyPayoutFilters = {}) {
  const params = compactParams(filters);
  return useQuery({
    queryKey: fiscalQueryKeys.payoutsFiltered(params),
    queryFn: () => fetchMyPayouts(params),
    placeholderData: keepPreviousData,
  });
}

export function useUploadFiscalDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ docType, file }: { docType: FiscalDocumentType; file: File }) =>
      uploadFiscalDocument(docType, file),
    onSuccess: (data) => {
      queryClient.setQueryData(fiscalQueryKeys.profile, data);
    },
  });
}

export function useDeleteFiscalDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (docType: FiscalDocumentType) => deleteFiscalDocument(docType),
    onSuccess: (data) => {
      queryClient.setQueryData(fiscalQueryKeys.profile, data);
    },
  });
}

// ── Admin: Registro Ritenuta ──────────────────────────────────────────────────

export const registroQueryKeys = {
  registro: (year: number, month: number) =>
    ['fiscal', 'admin', 'registro', year, month] as const,
};

async function fetchRegistro(year: number, month: number): Promise<RegistroRitenuta[]> {
  const response = await api.get<ApiResponse<RegistroRitenuta[]>>(
    `/fiscal/admin/registro?year=${year}&month=${month}`
  );
  return response.data.data;
}

async function markVersata(id: string, f24Protocollato: string): Promise<RegistroRitenuta> {
  const response = await api.post<ApiResponse<RegistroRitenuta>>(
    `/fiscal/admin/registro/${id}/versata`,
    { f24Protocollato }
  );
  return response.data.data;
}

async function runRollup(year: number, month: number): Promise<void> {
  await api.post(`/fiscal/admin/rollup/${year}/${month}`);
}

export function useRegistro(year: number | null, month: number | null) {
  return useQuery({
    queryKey: registroQueryKeys.registro(year ?? 0, month ?? 0),
    queryFn: () => fetchRegistro(year!, month!),
    enabled: year !== null && month !== null,
  });
}

export function useMarkVersata() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, f24Protocollato }: { id: string; f24Protocollato: string }) =>
      markVersata(id, f24Protocollato),
    onSuccess: (_data, variables, _ctx) => {
      // Invalidate all registro queries so the table refreshes
      queryClient.invalidateQueries({ queryKey: ['fiscal', 'admin', 'registro'] });
      void variables; // used only for typing
    },
  });
}

export function useRunRollup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ year, month }: { year: number; month: number }) => runRollup(year, month),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fiscal', 'admin', 'registro'] });
    },
  });
}

export async function downloadCuCsv(year: number): Promise<void> {
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';
  const url = `${base}/fiscal/admin/cu/${year}`;
  const token = tokenManager.getAccessToken();
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error(`Download CU fallito (HTTP ${res.status})`);
  }
  downloadBlob(await res.blob(), `CU-${year}.csv`);
}
